/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { fetchTransaction } from "@wagmi/core";

import { useRootContext } from "./app-context";
import {
  fetchApprovalAllowance,
  fetchIsSwappingActive,
  fetchTokenBalance,
  fetchVanryTokenBalance,
  getWaitTransaction,
} from "services/swap-tvk";
import {
  AddTrxToDB,
  FetchUserTrxData,
  getContractAndAbi,
  status,
} from "utils/helper";
import StatusModal from "components/modals/StakeModal";
import { SectionLoading } from "components/loader/Loading";

export const SwapContext = createContext(null);

const objHashType = {
  t_hash: "",
  type: "",
};

const objTrxData = {
  txId: "",
  to: "",
  from: "",
  tvk: "",
  txType: "",
  chainId: null,
};
const SwapContextProvider = ({ children }) => {
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSwappingActive, setIsSwappingActive] = useState(false);
  const [userTokenBalance, setUserTokenBalance] = useState(null);
  const [approvalAllowance, setApprovalAllowance] = useState(null);
  const [vanryTokenBalance, setVanryTokenBalance] = useState(null);
  const [stakingModalStatus, setStakingModalStatus] = useState("");
  const [approvalModalStatus, setApprovalModalStatus] = useState("");
  const [userTrxData, setUserTrxData] = useState([]);
  const [transactionHash, setTransactionHash] = useState("");
  const [trxHashType, setTrxHashType] = useState(
    JSON.parse(localStorage.getItem("txHashType")) || objHashType
  );
  const [trxData, setTrxData] = useState(objTrxData);
  const { address } = useAccount();
  const { isSignWallet, selectedNetwork } = useRootContext();

  const {
    tvkTokenAbi,
    tvkTokenContract,
    swapContract,
    swapAbi,
    vanryAbi,
    vanryContract,
  } = getContractAndAbi(selectedNetwork);

  const handleFetchTvkBalance = async () => {
    try {
      const balance = await fetchTokenBalance(address, tvkTokenContract);
      setUserTokenBalance(balance);
    } catch (error) {}
  };

  const handleLoading = () => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  const handleIsSwappingActive = async () => {
    const configObj = {
      swapContract: swapContract,
      tokenContractAbi: swapAbi,
    };
    const isSwapping = await fetchIsSwappingActive(configObj);
    setIsSwappingActive(isSwapping);
  };

  const handleFetchApprovalAllowance = async () => {
    try {
      const configObj = {
        tokenContract: tvkTokenContract,
        swapContract: swapContract,
        walletAddress: address,
        tokenContractAbi: tvkTokenAbi,
      };
      const allowance = await fetchApprovalAllowance(configObj);
      setApprovalAllowance(allowance);
    } catch (error) {}
  };
  
  const handleFetchVanryTokenBalance = async () => {
    try {
      const configObj = {
        vanryAbi: vanryAbi,
        vanryContract: vanryContract,
        walletAddress: address,
      };
      const vanryBalance = await fetchVanryTokenBalance(configObj);
      setVanryTokenBalance(vanryBalance);
    } catch (error) {}
  };

  const handleFetchUserData = async () => {
    try {
      const data = await FetchUserTrxData(address, selectedNetwork);
      setUserTrxData(data);
    } catch (error) {}
  };

  useEffect(() => {
    handleLoading();
  }, [isSignWallet]);

  const handleFetchTransaction = async () => {
    if (trxHashType.t_hash) {
      try {
        await fetchTransaction({ hash: trxHashType.t_hash });
      } catch (error) {
        const tempErr = JSON.parse(JSON.stringify(error));
        if (tempErr?.name === "TransactionNotFoundError") {
          localStorage.removeItem("txHashType");
          setTrxHashType(objHashType);
        }
      }
    }
  };

  useEffect(() => {
    handleFetchTransaction();
  }, [trxHashType.t_hash]);

  useEffect(() => {
    setInputValue("");
  }, [selectedNetwork, isSignWallet]);

  useEffect(() => {
    if (isSignWallet && address && selectedNetwork) {
      handleFetchUserData();
      let timeout = setTimeout(() => {
        handleFetchTvkBalance();
        handleFetchApprovalAllowance();
        handleFetchVanryTokenBalance();
        handleIsSwappingActive()
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [isSignWallet, address, selectedNetwork]);

  useEffect(() => {
    if (trxHashType.t_hash && isSignWallet) {
      if (trxHashType.type === status.SWAPPED) {
        setStakingModalStatus(status.PENDING);
      } else if (trxHashType.type === status.APPROVED_ALLOWANCE) {
        setApprovalModalStatus(status.PENDING);
      }
      setTransactionHash(trxHashType.t_hash);

      getWaitTransaction(trxHashType.t_hash).then((trx) => {
        if (trx) {
          if (trx?.status === "success") {
            if (trxHashType.type === status.APPROVED_ALLOWANCE) {
              handleFetchApprovalAllowance();
              setApprovalModalStatus(status.SUCCESS);
              AddTrxToDB(
                trxData.txId,
                trxData.to,
                trxData.from,
                trxData.tvk,
                trxData.txType,
                trxData.chainId
              );
            } else if (trxHashType.type === status.SWAPPED) {
              handleFetchTvkBalance();
              handleFetchApprovalAllowance();
              handleFetchVanryTokenBalance();

              setInputValue("");
              setStakingModalStatus(status.SUCCESS);
              AddTrxToDB(
                trxData.txId,
                trxData.to,
                trxData.from,
                trxData.tvk,
                trxData.txType,
                trxData.chainId
              ).then(() => {
                handleFetchUserData();
              });
            }
            setTrxHashType(objHashType);
            !transactionHash && setTransactionHash(trxHashType.t_hash);
            localStorage.removeItem("txHashType");
          } else {
            setTrxHashType(objHashType);
            localStorage.removeItem("txHashType");
            if (trxHashType.type === status.SWAPPED) {
              setStakingModalStatus(status.FAIL);
            } else if (trxHashType.type === status.APPROVED_ALLOWANCE) {
              setApprovalModalStatus(status.FAIL);
            }
          }
        }
      });
    }
  }, [trxHashType, isSignWallet]);

  return (
    <>
      <SwapContext.Provider
        value={{
          swapAbi,
          inputValue,
          trxHashType,
          userTrxData,
          vanryContract,
          tvkTokenContract,
          userTokenBalance,
          isSwappingActive,
          approvalAllowance,
          vanryTokenBalance,
          tvkAbi: tvkTokenAbi,
          tvkSwapContract: swapContract,
          setTrxData,
          setInputValue,
          setTrxHashType,
          setUserTokenBalance,
        }}
      >
        {loading ? (
          <SectionLoading />
        ) : (
          children
        )}

        {stakingModalStatus && (
          <StatusModal
            show={stakingModalStatus}
            title={
              stakingModalStatus === status.PENDING
                ? "Swapping"
                : stakingModalStatus === status.SUCCESS
                ? "Swapping Successful"
                : "Transaction failed"
            }
            status={stakingModalStatus}
            t_hash={transactionHash}
            onHide={() => {
              setStakingModalStatus("");
              setTransactionHash("");
            }}
          />
        )}
        {approvalModalStatus && (
          <StatusModal
            show={approvalModalStatus}
            title={
              approvalModalStatus === status.PENDING
                ? "Approval Pending"
                : approvalModalStatus === status.SUCCESS
                ? "Approval Successful"
                : "Transaction failed"
            }
            status={approvalModalStatus}
            t_hash={transactionHash}
            onHide={() => {
              setApprovalModalStatus("");
              setTransactionHash("");
            }}
          />
        )}
      </SwapContext.Provider>
    </>
  );
};

export default SwapContextProvider;

export const useSwapContext = () => {
  const {
    tvkAbi,
    swapAbi,
    inputValue,
    trxHashType,
    vanryContract,
    tvkSwapContract,
    userTokenBalance,
    isSwappingActive,
    tvkTokenContract,
    vanryTokenBalance,
    approvalAllowance,
    setTrxData,
    setInputValue,
    setTrxHashType,
    setUserTokenBalance,
    userTrxData,
  } = useContext(SwapContext);

  return {
    tvkAbi,
    swapAbi,
    inputValue,
    trxHashType,
    vanryContract,
    tvkSwapContract,
    userTokenBalance,
    isSwappingActive,
    tvkTokenContract,
    vanryTokenBalance,
    approvalAllowance,
    setTrxData,
    setInputValue,
    setTrxHashType,
    setUserTokenBalance,
    userTrxData,
  };
};
